import React, { useEffect, useRef, useState } from "react";
import i18next from "i18next";
import { SelectInput } from "../../SelectInput";
import { Overlay, Popover } from "react-bootstrap";
import { Link } from "react-router-dom";

import FontAwesomeIcon from "@fortawesome/react-fontawesome";
import faChevronRight from "@fortawesome/fontawesome-free-solid/faChevronRight";

import { ReportDataHelper } from "../../../helpers";
import { TimeFormattingService, EntityService } from "../../../services";

import config from "config";

const NADLocationJumperLabel = ({location}) => {
  return (
    <div className="location-jumper-option">
      <img src={`${config.WEB_BASE_URL}/images/icon/black-map-marker.png`} />
      <span className="option-name">{location.label}</span>
      {
        location && location.grouping && location.grouping.parent && location.grouping.parent.parent &&
          <span className="option-taxonomy">
          {
              location.grouping.parent.parent.parent ? (<span>
                  <strong>{location.grouping.parent.parent.name} <FontAwesomeIcon icon={faChevronRight} /></strong>
                  {location.grouping.parent.name}
                </span>) : (<span>{location.grouping.parent.name}</span>)
          }
          </span>
      }
    </div>
  );
}
  
const NADLocationJumper = ({isLoading, availableLocations, locationClicked}) => {
  const [formattedLocationOptions, setFormattedLocationOptions] = useState([]);

  useEffect(() => {
      setFormattedLocationOptions(
        availableLocations?.map((location) => {
          return {
            label: <NADLocationJumperLabel location={location} />,
            value: location.value,
            grouping: location.grouping,
          };
        })
      );
  }, [availableLocations]);

  return (
    <div className="row">
      <div className="col-sm-12">
        <SelectInput
          classNamePrefix={`${isLoading ? "loading-locations " : ""}location-jumper`}
          name={`location`}
          input={{
            value: isLoading
              ? {
                  label: (
                    <div className="select-box-placeholder">
                      {i18next.t(
                                "pages.locations-needing-attention.jumper.loading"
                              )}
                    </div>
                  ),
                }
              : {
                  label: (
                    <div className="select-box-placeholder">
                      {i18next.t(
                                "pages.locations-needing-attention.jumper.jump"
                              )}
                    </div>
                  ),
                },
            onChange: locationClicked,
          }}
          options={formattedLocationOptions}
          getOptionValue={(location) => location.grouping && location.grouping.name}
          isSearchable
          meta={{}}
          placeholder={<div>{i18next.t(
            "pages.locations-needing-attention.jumper.jump"
          )}</div>}
        />
      </div>
    </div>
  );
}

const NADLocationTableCell = ({location, locationClicked}) => {
  return (
    <div className="location-name-container">
      <div>
        {locationClicked ? <a onClick={() => { locationClicked({ grouping: { id: location.id } }); }}>{location.name}</a> : <strong>{location.name}</strong>}
        {location.grouping?.parent?.parent && (
          <span>
            {location.grouping.parent.parent.parent ? (
              <div>
                <span>{location.grouping.parent.parent.name}</span>{" "}
                <FontAwesomeIcon icon={faChevronRight} size="sm" />{" "}
                {location.grouping.parent.name}
              </div>
            ) : (
              <div>{location.grouping.parent.name}</div>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

const NADEventTableCellOverlay = ({target, showOverlay, alertId, occurredAt, displayName, entitySerialNumber, deviceDataTypeToken, thresholdClassificationToken, deviceDataTypeName, thresholdEvaluationTypeName, measureName}) => {
  const [displayValue, setDisplayValue] = useState(null);

  useEffect(() => {
    if (showOverlay && displayValue == null) {
      EntityService.getLastReceivedDataBySerialNumberAndDeviceType(entitySerialNumber, deviceDataTypeToken).then(({lastReceivedData}) => {
        var value = null;
        if (lastReceivedData.stringValue != null) {
          value = lastReceivedData.stringValue;
        } else if(lastReceivedData.numericValue != null) {
          value = lastReceivedData.numericValue;
        } else if(lastReceivedData.complexValue != null) {
          value = lastReceivedData.complexValue
        } else if (lastReceivedData.booleanValue != null) {
          value = `${lastReceivedData.booleanValue}`;
        }
        setDisplayValue(
          ReportDataHelper.applyDataTypeConverterAndFormat(
            value,
            lastReceivedData.deviceDataType?.deviceDataDisplayType?.token, 
            lastReceivedData.deviceDataType?.deviceDataDisplayType?.format
          )
        );
      }).catch((err) => {
        console.error(err);
        setDisplayValue('NA');
      });
    }
  }), [entitySerialNumber, deviceDataTypeToken];

  return (
    <Overlay target={target.current} key={alertId} placement="top" show={showOverlay}>
      <Popover tabIndex="1" className="map-popover ayeee" id="popover-trigger-hover-focus-no-width">
        <div>
          <div className="needs-attention-popover-header">
            <div>
              <span>{ occurredAt }</span>
              <span className="hover-right">{i18next.t("pages.locations-needing-attention.jumper.unacknowledged")}</span>
            </div>
          </div>
          <div>
            <div className="popover-top">
              <img src={`${config.WEB_BASE_URL}/images/icon/line_category.svg`} className="icon icon--medium" />
              <span>{ displayName }</span>
              <span className="actual-measurement">
                <div>
                  <div className="dot dot--failure dot--small bundle-dot" />
                </div>
                {displayValue != null ? <div>{ displayValue }</div> : <div>Loading</div>}
              </span>
            </div>
            <div className="popover-bottom">
              <div className="elbow">
                <img src={`${config.WEB_BASE_URL}/images/icon/elbow.svg`} className="icon icon--small" />
              </div>
              <div className="reading">
                <div>
                  <img src={`${config.WEB_BASE_URL}/images/icon/sensor_${thresholdClassificationToken}.svg`} height="19" />
                  <span className="data-type">{ deviceDataTypeName }</span>
                  <span className="threshold-evaluation">{ thresholdEvaluationTypeName }</span>
                  { measureName }
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popover>      
    </Overlay>
  );
}
            
const NADEventTableCell = ({isMobile, alertId, occurredAt, displayName, entitySerialNumber, deviceDataTypeToken, thresholdClassificationToken, thresholdClassificationName, deviceDataTypeName, thresholdEvaluationTypeName, measureName, alertConfigurationName, locationId}) => {
  const target = useRef(null);
  const [showOverlay, setShowOverlay] = useState(false);

  return (
    <div className="needs-attention-table-events">
      <div>
        <NADEventTableCellOverlay
          target={target}
          alertId={alertId} 
          occurredAt={occurredAt} 
          displayName={displayName}
          entitySerialNumber={entitySerialNumber}
          deviceDataTypeToken={deviceDataTypeToken}
          thresholdClassificationToken={thresholdClassificationToken} 
          deviceDataTypeName={deviceDataTypeName}
          thresholdEvaluationTypeName={thresholdEvaluationTypeName}
          measureName={measureName}
          alertConfigurationName={alertConfigurationName}
          locationId={locationId}
          showOverlay={showOverlay}
        />
        <Link to={`/monitoring/locations/${locationId}/events/${alertId}`}>
          <div 
            ref={target} 
            onMouseEnter={() => !showOverlay && setShowOverlay(true)} 
            onMouseLeave={() => showOverlay && setShowOverlay(false)} 
            onMouseOver={() => !showOverlay && setShowOverlay(true)}
            className="trigger"
          >
            { alertConfigurationName }
          </div>
        </Link>
      </div>

      <div className="danger">
        <img src={`${config.WEB_BASE_URL}/images/icon/sensor_${thresholdClassificationToken}.svg`} height="16" />
        { thresholdClassificationName }
      </div>
    </div>
  );
}

const NADNameTableCell = ({entity, onClick}) => {
  if (entity != null) {
      return (
        <div className="device-name">
          <div onClick={onClick} title={entity.serialNumber}>
            {entity.displayName}
          </div>
        </div>
      );
    } else {
      return <div></div>;
    }
}

const NADFormattedTime = ({locationTimezone, timestamp, newline}) => {
  return timestamp == null ? (<span>Unknown</span>) : (
    <span> 
      {TimeFormattingService.renderDate(timestamp, locationTimezone)} 
      &nbsp;{newline && <br />} 
      {TimeFormattingService.renderTime(timestamp, locationTimezone)} 
    </span>
  );
}

export {
    NADLocationJumper,
    NADLocationTableCell,
    NADEventTableCell,
    NADNameTableCell,
    NADFormattedTime
};